import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import {
  ESP_STALE_ACTIVITY_DAYS,
  EspActivitiesModal,
  EspRequestFormModal,
  findEspQuarterActivity,
  getEspFiscalQuarter,
  isEspPartnerVisible,
  parseEspDate,
  type TEspRequestType,
  useEspAccess,
  useEspSponsorshipStatus,
  useGetEspPartnerQuery,
  useGetEspSponsorListQuery,
} from 'partner360/features/executiveSponsorProgram'

import { CockpitEspChip } from './components/CockpitEspChip'
import { CockpitEspSheet, type ICockpitEspRequestNote } from './components/CockpitEspSheet'
import { usePartnerIsHeadquarters } from './hooks/usePartnerIsHeadquarters'

import styles from './CockpitEsp.module.scss'

const DAY_MS = 24 * 60 * 60 * 1000

/** Air between the top row and the panel's upper edge. */
const SHEET_GAP = 8

/** `new` logs an engagement; an activity id opens that engagement. */
type TEspModal = { kind: 'request'; mode: TEspRequestType } | { kind: 'activities'; target: string }

interface ICockpitEspProps {
  partnerId: string
  /** A PBM has signed a plan of the current fiscal year. */
  isPlanSigned: boolean
}

/**
 * The Executive Sponsor Program on the partner cockpit's top row: a chip with the sponsorship and
 * the panel it opens under the row. Shown to a partner in the program, and to a top partner whose
 * plan a PBM has signed, who may be nominated; nothing for anyone else.
 */
export const CockpitEsp = ({ partnerId, isPlanSigned }: ICockpitEspProps) => {
  const hasEspAccess = useEspAccess()
  const espPartnerId = hasEspAccess ? partnerId : ''
  const { status, sponsorship, requests, isLoading } = useEspSponsorshipStatus(espPartnerId)
  const { data: espPartner } = useGetEspPartnerQuery(
    { partnerId: espPartnerId },
    { skip: !espPartnerId }
  )
  const isHeadquarters = usePartnerIsHeadquarters(espPartnerId)
  const isActive = status === 'active'
  const { data: sponsorList } = useGetEspSponsorListQuery(undefined, { skip: !isActive })

  const [isOpen, setIsOpen] = useState(false)
  const [modal, setModal] = useState<TEspModal | null>(null)
  const chipRef = useRef<HTMLButtonElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const [sheetTop, setSheetTop] = useState(0)

  const sponsorName = sponsorship?.currentSponsor?.name ?? ''
  const sponsorTitle = useMemo(
    () => sponsorList?.find(({ Name }) => Name === sponsorName)?.Title ?? '',
    [sponsorList, sponsorName]
  )

  const findPending = useCallback(
    (type: TEspRequestType): ICockpitEspRequestNote | null => {
      const request = requests.find((item) => item.type === type && item.status === 'pending')

      return request ? { sponsorName: request.sponsor.name, createdAt: request.createdAt } : null
    },
    [requests]
  )

  const activities = useMemo(() => espPartner?.activities ?? [], [espPartner?.activities])
  const daysSinceLast = useMemo(() => {
    const latest = activities.reduce<string | null>(
      (last, { date }) => (last && last > date ? last : date),
      null
    )

    if (!latest) {
      return null
    }

    const today = new Date()
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())

    return Math.max(
      0,
      Math.round((startOfToday.getTime() - parseEspDate(latest).getTime()) / DAY_MS)
    )
  }, [activities])

  const loggedQuarter = useMemo(() => {
    const today = new Date()

    return findEspQuarterActivity(activities, today) ? getEspFiscalQuarter(today).label : null
  }, [activities])

  /* The panel hangs from the row the chip stands in, across the deck it is drawn in. */
  useLayoutEffect(() => {
    const chip = chipRef.current
    const host = sheetRef.current?.offsetParent

    if (!isOpen || !chip || !host) {
      return
    }

    const place = () => {
      const row = chip.parentElement?.closest('div') ?? chip
      setSheetTop(row.getBoundingClientRect().bottom - host.getBoundingClientRect().top + SHEET_GAP)
    }

    place()
    window.addEventListener('resize', place)

    return () => window.removeEventListener('resize', place)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !modal) {
        setIsOpen(false)
        chipRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, modal])

  useEffect(() => {
    setIsOpen(false)
    setModal(null)
  }, [partnerId])

  if (
    !espPartnerId ||
    isLoading ||
    !isEspPartnerVisible({ status, isHeadquarters, isPlanSigned })
  ) {
    return null
  }

  const closeModal = () => setModal(null)

  return (
    <span className={styles.root}>
      <CockpitEspChip
        ref={chipRef}
        status={status}
        sponsorName={sponsorName}
        isOpen={isOpen}
        onToggle={() => setIsOpen((value) => !value)}
      />

      {isOpen && (
        <CockpitEspSheet
          ref={sheetRef}
          status={status}
          sponsorName={sponsorName}
          sponsorTitle={sponsorTitle}
          nomination={findPending('enrollment')}
          change={findPending('change')}
          activities={activities}
          daysSinceLast={daysSinceLast}
          isLate={daysSinceLast == null || daysSinceLast >= ESP_STALE_ACTIVITY_DAYS}
          loggedQuarter={loggedQuarter}
          top={sheetTop}
          onNominate={() => setModal({ kind: 'request', mode: 'enrollment' })}
          onLog={() => setModal({ kind: 'activities', target: 'new' })}
          onOpenActivity={(activityId) => setModal({ kind: 'activities', target: activityId })}
          onRequestChange={() => setModal({ kind: 'request', mode: 'change' })}
          onClose={() => {
            setIsOpen(false)
            chipRef.current?.focus()
          }}
        />
      )}

      <EspRequestFormModal
        show={modal?.kind === 'request'}
        partnerId={partnerId}
        mode={modal?.kind === 'request' ? modal.mode : 'enrollment'}
        onClose={closeModal}
      />

      {modal?.kind === 'activities' && (
        <EspActivitiesModal
          show
          partnerId={partnerId}
          initialFormTarget={modal.target}
          onClose={closeModal}
        />
      )}
    </span>
  )
}

CockpitEsp.displayName = 'CockpitEsp'
